import React, { memo, useEffect, useRef } from 'react'
import DOMPurify from 'dompurify'
import { twMerge } from 'tailwind-merge'

interface MessageEditableProps {
  id: string
  initialHTML: string
  className?: string
  onChange?: (text: string) => void
  onSubmit?: () => void
  onCancel?: () => void
}

const sanitizeHtml = (html: string): string =>
  DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['style', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['style', 'srcdoc'],
  }) as string

export const MessageEditable = memo(function MessageEditable({
  id,
  initialHTML,
  className,
  onChange,
  onSubmit,
  onCancel,
}: MessageEditableProps) {
  const editableDivRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!editableDivRef.current) return

    editableDivRef.current.innerHTML = sanitizeHtml(initialHTML)

    const links = editableDivRef.current.querySelectorAll('a')
    links.forEach((link) => {
      const originalHref = link.getAttribute('href')

      ;(link as HTMLElement).style.textDecoration = 'underline'
      ;(link as HTMLElement).style.cursor = 'pointer'
      ;(link as HTMLElement).title = 'Click to open link, Ctrl+Click to edit'

      link.addEventListener('click', (e) => {
        if ((e as MouseEvent).ctrlKey || (e as MouseEvent).metaKey) {
          e.preventDefault()
        } else {
          e.preventDefault()
          if (originalHref) {
            window.open(originalHref, '_blank')
          }
        }
      })
    })

    editableDivRef.current.focus()
    const selection = window.getSelection()
    const range = document.createRange()

    if (editableDivRef.current.childNodes.length > 0) {
      const lastChild = editableDivRef.current.lastChild
      if (lastChild) {
        range.setStartAfter(lastChild)
      } else {
        range.setStart(editableDivRef.current, 0)
      }
    } else {
      range.setStart(editableDivRef.current, 0)
    }

    range.collapse(true)
    selection?.removeAllRanges()
    selection?.addRange(range)

    const currentText = editableDivRef.current.innerText || ''
    onChange?.(currentText)
  }, [initialHTML])

  return (
    <div className={twMerge('relative', className)}>
      <div
        id={id}
        ref={editableDivRef}
        // The message's own markdown typography, since the snapshot it edits is that markup. `block`
        // and `whitespace-pre-wrap` because it's a contenteditable: typed newlines stay put.
        className={twMerge(
          'markdown block whitespace-pre-wrap',
          'relative z-10 rounded-md',
          '!selection:text-foreground',
        )}
        role="textbox"
        aria-multiline={true}
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        onKeyDown={(e) => {
          const ke = e as React.KeyboardEvent<HTMLDivElement>
          if (ke.key === 'Backspace') {
            const el = editableDivRef.current
            const text = el?.innerText || ''
            if (text.trim().length === 0) {
              ke.preventDefault()
              ke.stopPropagation()
              return
            }
          }
          if (ke.key === 'Enter' && !ke.shiftKey) {
            ke.preventDefault()
            onSubmit?.()
          } else if (ke.key === 'Escape') {
            ke.preventDefault()
            onCancel?.()
          }
        }}
        onInput={(e) => {
          const el = e.currentTarget as HTMLDivElement
          const text = el.innerText || ''
          onChange?.(text)
        }}
      />
    </div>
  )
})
